import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Car, Users, Receipt, AlertTriangle, TrendingUp, Wrench, UserCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/dashboard/stats");
        setStats(res.data);
        if (user?.role === "admin") {
          try {
            const logRes = await api.get("/logs?limit=10");
            setLogs(logRes.data);
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  if (loading) return <div className="text-slate-500" data-testid="dashboard-loading">Chargement du tableau de bord...</div>;

  const statCards = [
    { label: "Vehicules", value: stats.total_vehicules || 0, icon: Car, color: "text-[#0F52BA]", bg: "bg-blue-50" },
    { label: "Actifs", value: stats.vehicules_actifs || 0, icon: UserCheck, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Immobilises", value: stats.vehicules_immobilises || 0, icon: Wrench, color: "text-red-600", bg: "bg-red-50" },
    { label: "Chauffeurs", value: stats.total_chauffeurs || 0, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Recettes du jour", value: `${stats.recettes_jour || 0} Frc`, icon: Receipt, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Recettes semaine", value: `${stats.recettes_semaine || 0} Frc`, icon: TrendingUp, color: "text-[#0F52BA]", bg: "bg-blue-50" },
    { label: "Dettes en cours", value: `${stats.total_dettes || 0} Frc`, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Alertes actives", value: stats.alertes_actives || 0, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
  ];

  if (user?.role === "admin") {
    statCards.splice(3, 0, { label: "Gestionnaires", value: stats.total_gestionnaires || 0, icon: Users, color: "text-[#0F52BA]", bg: "bg-blue-50" });
  }

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
          <p className="text-sm text-slate-500 mt-1">
            Bienvenue, {user?.prenom} {user?.nom}
            {user?.role === "admin" && <Badge variant="outline" className="ml-2 text-[#0F52BA] border-[#0F52BA]">Admin</Badge>}
            {user?.role === "gestionnaire" && <Badge variant="outline" className="ml-2">Gestionnaire</Badge>}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="stat-card" data-testid={`stat-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${s.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 font-heading">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      {stats.chart_data && stats.chart_data.length > 0 && (
        <Card className="mb-8" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-slate-900">Recettes des 7 derniers jours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.chart_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748B" }} tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
                  <Tooltip formatter={(v) => [`${v} Frc`, "Recettes"]} labelFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`; }} />
                  <Bar dataKey="montant" fill="#0F52BA" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Logs */}
      {user?.role === "admin" && logs.length > 0 && (
        <Card data-testid="recent-logs">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-slate-900">Activite recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {logs.map((log, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-[#0F52BA] mt-2 shrink-0" />
                  <div>
                    <p className="text-sm text-slate-700">{log.details || log.action}</p>
                    <p className="text-xs text-slate-400">{new Date(log.timestamp).toLocaleString("fr-FR")}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
